/**
 * RecurringDetailModal.tsx
 *
 * Recurring-payment detail modal matching the PHP layout:
 * title card with membership info at top, billing/payment section,
 * payment history, with actions in a fixed footer.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Save, Snowflake, RotateCcw, Ban, RefreshCw, AlertTriangle, Loader2, CheckCircle2, ChevronDown, FileText, Plus, Search,
} from 'lucide-react';
import {
  getFreezeRecords,
  getPaymentHistory,
  getRecurringDetail,
  getTemplate,
  listCancellationReasons,
  retryRecurringPayment,
  saveFreezeRecords,
  saveRecurringPayment,
  unfreezeAndCharge,
  cancelRecurringPayment,
  adjustCancelDate,
  reactivateRecurring,
  addEnrollmentItem,
  searchProducts,
  type CancellationReason,
  type FreezeRecord,
  type MembershipProduct,
  type MembershipTemplate,
  type PaymentHistoryRecord,
  type ProductSearchResult,
  type RecurringChargeResult,
  type RecurringDetailData,
} from '@/services/memberships.service';
import { getErrorMessage } from '@/services/apiClient';
import { toYmd, todayYmd } from '@/lib/utils';
import { listFacilities } from '@/services/admin.service';
import {
  type BillingMethod,
  type PaymentTerminal,
} from '@/services/payments.service';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { MerchantSelector } from '@/components/payments/MerchantSelector';
import {
  PaymentSourceSelector,
  parseTerminalToken,
} from '@/components/payments/PaymentSourceSelector';
import { ForwardBillingMethodCapture } from '@/components/payments/ForwardBillingMethodCapture';
import {
  TerminalCheckoutPanel,
  type TerminalFlowResult,
} from '@/components/payments/TerminalCheckoutPanel';
import { useAuth } from '@/hooks/useAuth';
import { PaymentDetailModal } from '@/pages/payments/PaymentDetailModal';
import { AppointmentDetailModal } from '@/pages/appointments/AppointmentDetailModal';
import { appointmentsService, type Appointment } from '@/services/appointments.service';
import { getPatientProductTypes } from '@/services/patientProductTypes.service';
import {
  ProductGroupCard,
  toArray,
  type ProductGroup,
  type ProductItem,
  type ProductTypesResponse,
} from '@/components/patients/PatientProductsDisplay';
import { DatePicker } from '@/components/ui/DatePicker';

/* ── Utility helpers ───────────────────────────────────────────────── */

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return fmtDate(value);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    + ' at '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtCurrency(value: number | null | undefined): string {
  if (value == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function normalizeHistoryStatus(raw: string): string {
  const s = raw.toLowerCase().trim();
  if (s === 'settledsuccessfully') return 'Settled';
  if (s === 'capturedpendingsettlement') return 'Pending';
  if (s === 'refundpendingsettlement') return 'Refund Pending';
  if (s === 'refundsettledsuccessfully') return 'Refunded';
  if (s === '') return 'No Charge';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function toDateInput(value: string | null | undefined): string {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

function filterProductsByMembership(
  data: ProductTypesResponse,
  membershipIds: Set<number>,
): { types: ProductGroup[]; others: ProductGroup[] } {
  const filterItems = (items: ProductItem[]) =>
    items.filter((i) => i.membershipID != null && membershipIds.has(i.membershipID));

  function filterGroup(group: ProductGroup): ProductGroup | null {
    const available = filterItems(toArray<ProductItem>(group.available));
    const upcoming = filterItems(toArray<ProductItem>(group.upcoming));
    const appointments = filterItems(toArray<ProductItem>(group.appointments));
    const comped = filterItems(toArray<ProductItem>(group.comped));
    const purchase = filterItems(toArray<ProductItem>(group.purchase));
    const unavailable = filterItems(toArray<ProductItem>(group.unavailable));
    const onHold = filterItems(toArray<ProductItem>(group.onHold));

    const total = available.length + upcoming.length + appointments.length
      + comped.length + purchase.length + unavailable.length + onHold.length;
    if (total === 0) return null;

    const usedQty = unavailable
      .filter((p) => p.usedDate && p.usedDate !== '0000-00-00 00:00:00')
      .reduce((s, p) => s + p.quantity, 0);
    const expiredOrDeleted = unavailable.filter(
      (p) => !p.usedDate || p.usedDate === '0000-00-00 00:00:00',
    );
    const expireQty = expiredOrDeleted
      .filter((p) => !p.deleteDate || p.deleteDate === '0000-00-00 00:00:00')
      .reduce((s, p) => s + p.quantity, 0);
    const deleteQty = expiredOrDeleted
      .filter((p) => p.deleteDate && p.deleteDate !== '0000-00-00 00:00:00')
      .reduce((s, p) => s + p.quantity, 0);

    return {
      ...group,
      available,
      upcoming,
      appointments,
      comped,
      compedQuantity: comped.reduce((s, p) => s + p.quantity, 0),
      purchase,
      unavailable,
      onHold,
      onHoldQuantity: onHold.reduce((s, p) => s + p.quantity, 0),
      availableQuantity: available.reduce((s, p) => s + p.quantity, 0),
      upcomingQuantity: upcoming.reduce((s, p) => s + p.quantity, 0),
      appointmentsQuantity: appointments.reduce((s, p) => s + p.quantity, 0),
      usedQuantity: usedQty,
      expireQuantity: expireQty,
      deleteQuantity: deleteQty,
    };
  }

  const rawTypes = data?.data?.['product-types'] ?? {};
  const rawOthers = data?.data?.['product-others'] ?? {};

  const types = Object.values(rawTypes)
    .map(filterGroup)
    .filter((g): g is ProductGroup => g !== null);
  const others = Object.values(rawOthers)
    .map(filterGroup)
    .filter((g): g is ProductGroup => g !== null);

  return { types, others };
}

function chargeResultMessage(action: string, result?: RecurringChargeResult): { tone: 'success' | 'warning'; text: string } {
  if (!result) return { tone: 'success', text: `${action} completed.` };
  if (result.status === 'declined') return { tone: 'warning', text: `${action} declined. Review the payment source and try again.` };
  if (result.status === 'no_due_record') return { tone: 'warning', text: `${action} found no due billing record to process.` };
  if (result.status === 'no_retry_record') return { tone: 'warning', text: `${action} found no declined billing record to retry.` };
  if (result.paymentId) return { tone: 'success', text: `${action} completed successfully. Payment #${result.paymentId} was recorded.` };
  return { tone: 'success', text: `${action} completed with status "${result.status}".` };
}

function toneClasses(tone: 'success' | 'warning' | 'error'): string {
  switch (tone) {
    case 'warning': return 'bg-amber-50 border-amber-200 text-amber-800';
    case 'error': return 'bg-red-50 border-red-200 text-red-700';
    default: return 'bg-green-50 border-green-200 text-green-700';
  }
}

/* ── Status Banner (matching PHP notification bar) ─────────────────── */

function StatusBanner({
  detail,
  onUnfreeze,
}: {
  detail: RecurringDetailData;
  onUnfreeze: () => void;
}) {
  const { currentRecordStatus, incrementType, frozenFromDate, frozenToDate, firstBillingDate, nextBillingDate, pauseUntilDate } = detail;
  const today = todayYmd();

  if (currentRecordStatus === 'frozen' && incrementType !== '0') {
    return (
      <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-700">
        <span>FROZEN {fmtDate(frozenFromDate)} - {fmtDate(frozenToDate)}</span>
        <Button size="xs" variant="secondary" leftIcon={<Snowflake className="h-3 w-3" />} onClick={onUnfreeze}>
          Unfreeze
        </Button>
      </div>
    );
  }

  if (currentRecordStatus === '' && incrementType !== '0') {
    const firstYmd = firstBillingDate ? firstBillingDate.split(' ')[0] : null;
    if (firstYmd && firstYmd > today) {
      return (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm font-medium text-green-700">
          This Membership Starts on {fmtDate(firstBillingDate)}
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-700">
        PAUSED Until {fmtDate(nextBillingDate ?? pauseUntilDate)}
      </div>
    );
  }

  if (incrementType === '0') {
    const { endDate, reactivationThroughDate } = detail;
    const endYmd = endDate ? endDate.split(' ')[0] : null;
    const reactivationYmd = reactivationThroughDate ? reactivationThroughDate.split(' ')[0] : null;
    const canStillReactivate = reactivationYmd ? reactivationYmd >= today : (endYmd ? endYmd > today : false);
    if (canStillReactivate) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700">
          Set to Cancel on {fmtDate(reactivationThroughDate ?? detail.paymentThroughDate)}
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700">
        CANCELED
      </div>
    );
  }

  return null;
}

/* ── Membership Title Card (section 1) ─────────────────────────────── */

function MembershipTitleCard({
  detail,
  templateProducts,
  nextBillingDate,
  onNextBillingChange,
  monthlyAmount,
  onAmountChange,
  startDate,
  onStartDateChange,
  isRecurring,
  amountEditable,
  statusDisplay,
  reasons,
  cancelReasonId,
  cancelOtherText,
  onCancelReasonChange,
  onCancelOtherTextChange,
  showRawEndDate,
  showCanceledBy,
}: {
  detail: RecurringDetailData;
  templateProducts: MembershipProduct[];
  nextBillingDate: string;
  onNextBillingChange: (value: string) => void;
  monthlyAmount: string;
  onAmountChange: (value: string) => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  isRecurring: boolean;
  amountEditable: boolean;
  statusDisplay: { text: string; color: string } | null;
  reasons: CancellationReason[];
  cancelReasonId: string;
  cancelOtherText: string;
  onCancelReasonChange: (value: string) => void;
  onCancelOtherTextChange: (value: string) => void;
  /** Level 5 only: also show the raw payments_recurring_authnet.endDate. */
  showRawEndDate: boolean;
  /** Level 3+ only: show which staff member cancelled. */
  showCanceledBy: boolean;
}) {
  const isCanceled = detail.incrementType === '0';
  const todayStr = todayYmd();
  const firstYmd = detail.firstBillingDate ? detail.firstBillingDate.split(' ')[0].split('T')[0] : null;
  // Editable only while the membership has NOT started yet (start date strictly
  // in the future). A start date of today means it has begun (and, for an
  // immediate enrollment, already charged), so the field locks. Uses strict '>'
  // to match the "starts on" checks in StatusBanner / statusDisplay.
  const isStartEditable = !!(firstYmd && firstYmd > todayStr && !isCanceled);

  // The picker only lists active reasons, so a cancellation recorded against a
  // since-deactivated reason has no matching option. Keep it as an option of its
  // own rather than letting the select fall back to another value and silently
  // rewrite the reason on the next save.
  const inactiveReasonId = useMemo(() => {
    if (!isCanceled || !cancelReasonId || cancelReasonId === '0' || cancelReasonId === 'other') return null;
    return reasons.some((r) => String(r.reasonId) === cancelReasonId) ? null : cancelReasonId;
  }, [isCanceled, cancelReasonId, reasons]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
        <div className="flex items-start justify-between gap-4">
          {/* Left: products per cycle */}
          <div className="min-w-0 flex-1">
            <p className="mb-2 text-xs font-medium text-slate-400">
              Products included per cycle
            </p>
            {templateProducts.length > 0 ? (
              <div className="space-y-1">
                {templateProducts.map((p) => (
                  <div key={p.templateProductId} className="flex items-baseline gap-3 text-base">
                    <span className="w-8 text-right font-semibold text-slate-900 tabular-nums">{p.quantity}</span>
                    <span className="text-slate-800">{p.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No products included</p>
            )}
          </div>

          {/* Right: amount per month + start date + status */}
          <div className="flex-shrink-0 text-right">
            <p className="text-xs font-medium text-slate-500">Amount / Month</p>
            {isRecurring && amountEditable ? (
              <input
                type="text"
                inputMode="decimal"
                value={`$${monthlyAmount}`}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9.]/g, '');
                  const parts = raw.split('.');
                  const cleaned = parts.length > 2
                    ? parts[0] + '.' + parts.slice(1).join('')
                    : raw;
                  onAmountChange(cleaned);
                }}
                onBlur={() => {
                  const num = parseFloat(monthlyAmount);
                  if (!Number.isNaN(num)) onAmountChange(num.toFixed(2));
                }}
                className="mt-1 w-32 border-0 border-b-2 border-transparent bg-transparent text-right text-2xl font-bold text-slate-900 focus:border-blue-500 focus:outline-none"
              />
            ) : (
              <p className="mt-1 text-2xl font-bold text-slate-900">
                {fmtCurrency(parseFloat(monthlyAmount) || 0)}
              </p>
            )}
            {statusDisplay && (
              <p className={`mt-1 text-xs font-semibold ${statusDisplay.color}`}>
                {statusDisplay.text}
              </p>
            )}
            <div className="mt-3">
              <div className="text-xs font-medium text-slate-500">Start Date</div>
              {isStartEditable ? (
                <DatePicker
                  size="sm"
                  value={startDate}
                  min={todayStr}
                  onChange={onStartDateChange}
                  ariaLabel="Start date"
                  wrapperClassName="mt-1 w-40"
                />
              ) : (
                <div className="mt-1 text-sm text-slate-800">{fmtDate(detail.firstBillingDate)}</div>
              )}
            </div>
          </div>
        </div>

        {!isStartEditable && (
          <div className={`mt-4 grid gap-4 border-t border-slate-100 pt-3 ${isCanceled ? 'sm:grid-cols-4' : 'sm:grid-cols-2'}`}>
            <div>
              <div className="text-xs font-medium text-slate-500">Paid Through</div>
              <div className="mt-1 text-sm text-slate-800">{fmtDate(detail.paymentThroughDate)}</div>
            </div>
            {isCanceled ? (
              <>
                {/* Two distinct dates: when the cancellation was submitted vs.
                    the date coverage actually ends. Showing only the latter
                    (the old "Cancel Date") read as the day the patient
                    canceled. The effective date is the last billed record's
                    paid-through date, the same value the banner and status
                    line use for "Set to Cancel on", NOT endDate. */}
                <div>
                  <div className="text-xs font-medium text-slate-500" title="When the cancellation was submitted">
                    Canceled On
                  </div>
                  <div className="mt-1 text-sm text-slate-800">
                    {fmtDate(detail.cancelRequestedDate)}
                  </div>
                  {showCanceledBy && (
                    <div className="mt-0.5 text-xs text-slate-400">
                      {detail.canceledByName
                        ? `by ${detail.canceledByName}`
                        : /* userID 0: a patient app cancel, a system auto-cancel, or a
                             cancellation recorded before userID tracking existed. */
                          <span className="italic">by unknown</span>}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500" title="Paid through the last billed cycle — the membership ends on this date">
                    Cancel Effective
                  </div>
                  <div className="mt-1 text-sm text-red-600">
                    {fmtDate(detail.reactivationThroughDate ?? detail.paymentThroughDate)}
                  </div>
                  {showRawEndDate && (
                    <div
                      className="mt-0.5 text-xs text-slate-400"
                      title="payments_recurring_authnet.endDate — the stop date this cancellation is scheduled for (visible to level 5 only)"
                    >
                      Scheduled: {fmtDate(detail.endDate)}
                    </div>
                  )}
                </div>
                {/* Editable after the fact, the same way the cancellation date
                    can be adjusted: saving a different reason closes the current
                    payments_recurring_cancel row and opens a replacement that
                    keeps the original cancel date and canceller. */}
                <div>
                  <label className="block text-xs font-medium text-slate-500">
                    Cancel Reason
                  <select
                    value={cancelReasonId || '0'}
                    onChange={(e) => onCancelReasonChange(e.target.value)}
                    className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="0">Select one...</option>
                    {reasons.map((r) => (
                      <option key={r.reasonId} value={String(r.reasonId)}>{r.reason}</option>
                    ))}
                    {inactiveReasonId && (
                      <option value={inactiveReasonId}>Current reason (no longer offered)</option>
                    )}
                    <option value="other">Other</option>
                  </select>
                  </label>
                  {cancelReasonId === 'other' && (
                    <input
                      value={cancelOtherText}
                      onChange={(e) => onCancelOtherTextChange(e.target.value)}
                      placeholder="Describe reason..."
                      maxLength={150}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  )}
                </div>
              </>
            ) : (
              <div>
                <div className="text-xs font-medium text-slate-500">Next Billing</div>
                <DatePicker
                  size="sm"
                  value={nextBillingDate}
                  min={toDateInput(detail.nextBillingMinDate) || undefined}
                  max={toDateInput(detail.nextBillingMaxDate) || undefined}
                  onChange={onNextBillingChange}
                  ariaLabel="Next billing date"
                  wrapperClassName="mt-1 w-40"
                />
              </div>
            )}
          </div>
        )}
    </div>
  );
}

/* ── Freeze Records Modal (separate popup matching PHP) ────────────── */

function FreezeRecordsModal({
  recurringId,
  open,
  onClose,
  onSaved,
}: {
  recurringId: number;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [records, setRecords] = useState<FreezeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getFreezeRecords(recurringId).then(setRecords).finally(() => setLoading(false));
  }, [recurringId, open]);

  const toggleFreeze = (idx: number) => {
    const updated = [...records];
    const rec = updated[idx];
    updated[idx] = { ...rec, status: rec.status === 'frozen' ? 'active' : 'frozen' };
    setRecords(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveFreezeRecords(recurringId, records.map((r) => ({
        paymentsRecurringRecordID: r.paymentsRecurringRecordID,
        status: r.status,
        paymentAmount: r.paymentAmount,
      })));
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Freeze Membership"
      size="sm"
      footer={
        <Button size="sm" onClick={() => void handleSave()} disabled={saving} loading={saving}>
          Save Records
        </Button>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-slate-400">Loading records...</span>
        </div>
      ) : records.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-400">No upcoming records.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto -mx-1">
          <div className="space-y-1.5 px-1">
            {records.map((rec, idx) => {
              const isFrozen = rec.status === 'frozen';
              return (
                <div
                  key={rec.paymentsRecurringRecordID}
                  className={`flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors ${
                    isFrozen ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isFrozen && <Snowflake className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                    <span className={`text-sm font-medium ${isFrozen ? 'text-blue-700' : 'text-slate-700'}`}>
                      {fmtDate(rec.paymentDate)}
                    </span>
                    <span className={`text-xs ${isFrozen ? 'text-blue-500' : 'text-slate-500'}`}>
                      {fmtCurrency(rec.paymentAmount)}
                    </span>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={isFrozen}
                    onClick={() => toggleFreeze(idx)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                      isFrozen ? 'bg-blue-500' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${
                        isFrozen ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ── Cancel Modal (separate popup) ─────────────────────────────────── */

function CancelModal({
  detail,
  reasons,
  open,
  onClose,
  onConfirm,
  cancelling,
}: {
  detail: RecurringDetailData;
  reasons: CancellationReason[];
  open: boolean;
  onClose: () => void;
  onConfirm: (reasonId: string, otherText: string, cancelDate: string) => void;
  cancelling: boolean;
}) {
  const [reasonId, setReasonId] = useState<string>('0');
  const [otherText, setOtherText] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [cancelDate, setCancelDate] = useState('');
  const [preset, setPreset] = useState('0');

  const today = todayYmd();
  const { cancelDuration, requiredDuration, createDate } = detail;

  useEffect(() => {
    if (!open) return;
    setReasonId('0');
    setOtherText('');
    setValidationError(null);

    const addDays = (d: Date, n: number) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
    const fmt = toYmd;

    const cancelNoticeDate = addDays(new Date(), cancelDuration);
    let requiredEndDate = new Date();
    const startYmd = createDate ? createDate.split(' ')[0] : '';
    if (requiredDuration > 0 && startYmd && startYmd !== '0000-00-00') {
      requiredEndDate = addDays(new Date(startYmd + 'T00:00:00'), requiredDuration);
    }

    let defaultDate = cancelNoticeDate;
    let defaultPreset = cancelDuration > 0 ? String(cancelDuration) : '0';

    if (requiredEndDate > cancelNoticeDate) {
      defaultDate = requiredEndDate;
      defaultPreset = 'custom';
    }

    if (fmt(defaultDate) <= today) {
      defaultDate = new Date();
      defaultPreset = '0';
    }

    setCancelDate(fmt(defaultDate));
    setPreset(defaultPreset);
  }, [open, cancelDuration, requiredDuration, createDate, today]);

  const handlePresetChange = (value: string) => {
    setPreset(value);
    if (value === 'custom') return;
    const days = parseInt(value, 10);
    const d = new Date();
    d.setDate(d.getDate() + days);
    setCancelDate(toYmd(d));
  };

  const canConfirm = reasonId !== '0' && (reasonId !== 'other' || otherText.trim().length > 0) && !!cancelDate;

  const handleConfirm = () => {
    if (reasonId === '0') { setValidationError('Please select a cancellation reason.'); return; }
    if (reasonId === 'other' && !otherText.trim()) { setValidationError('Please describe the cancellation reason.'); return; }
    setValidationError(null);
    onConfirm(reasonId, otherText, cancelDate);
  };

  const requiredEndYmd = (() => {
    const startYmd = createDate ? createDate.split(' ')[0] : '';
    if (requiredDuration > 0 && startYmd && startYmd !== '0000-00-00') {
      const d = new Date(startYmd + 'T00:00:00');
      d.setDate(d.getDate() + requiredDuration);
      return d;
    }
    return null;
  })();
  const showRequiredMsg = requiredEndYmd && toYmd(requiredEndYmd) > today;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Cancel Membership"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose}>Back</Button>
          <Button size="sm" variant="danger" onClick={handleConfirm} disabled={!canConfirm || cancelling} loading={cancelling}>Confirm Cancel</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1 text-sm text-slate-600">
          <p>Canceling this membership will stop recurring payments on the selected cancellation date.</p>
          <p>Any scheduled payments before the cancellation date will still be charged.</p>
          {cancelDuration > 0 && (
            <p className="text-slate-500">This membership requires <strong>{cancelDuration} days</strong> advance notice to cancel.</p>
          )}
          {showRequiredMsg && requiredEndYmd && (
            <p className="text-slate-500">This membership has a required duration of <strong>{requiredDuration} days</strong> (ends {fmtDate(toYmd(requiredEndYmd))}).</p>
          )}
        </div>

        {validationError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{validationError}</div>
        )}

        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">Cancellation Date</label>
          <div className="flex gap-2">
            <select
              value={preset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="0">Now</option>
              {cancelDuration > 0 && <option value={String(cancelDuration)}>{cancelDuration} Days</option>}
              {cancelDuration !== 30 && <option value="30">30 Days</option>}
              {cancelDuration !== 60 && <option value="60">60 Days</option>}
              {cancelDuration !== 90 && <option value="90">90 Days</option>}
              <option value="custom">Custom</option>
            </select>
            <DatePicker
              value={cancelDate}
              min={today}
              onChange={(value) => { setCancelDate(value); setPreset('custom'); }}
              ariaLabel="Cancellation date"
              wrapperClassName="flex-1"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">Cancel Reason <span className="text-red-500">*</span></label>
          <select
            value={reasonId}
            onChange={(e) => { setReasonId(e.target.value); setValidationError(null); }}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="0">Select one...</option>
            {reasons.map((r) => <option key={r.reasonId} value={String(r.reasonId)}>{r.reason}</option>)}
            <option value="other">Other</option>
          </select>

          {reasonId === 'other' && (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">Describe reason <span className="text-red-500">*</span></label>
              <input
                value={otherText}
                onChange={(e) => { setOtherText(e.target.value); setValidationError(null); }}
                placeholder="Describe reason..."
                maxLength={150}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ── Adjust Cancel Date Modal ─────────────────────────────────────── */

function AdjustCancelDateModal({
  open,
  onClose,
  onConfirm,
  currentCancelDate,
  adjusting,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (newDate: string) => void;
  currentCancelDate: string;
  adjusting: boolean;
}) {
  const [newDate, setNewDate] = useState('');
  const today = todayYmd();

  useEffect(() => {
    if (open) {
      setNewDate(currentCancelDate ? currentCancelDate.split(' ')[0] : today);
    }
  }, [open, currentCancelDate, today]);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Adjust Cancellation Date"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose}>Back</Button>
          <Button size="sm" onClick={() => onConfirm(newDate)} disabled={!newDate || adjusting} loading={adjusting}>Save</Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">Adjust the effective cancellation date. Upcoming records will be regenerated accordingly.</p>
        <div>
          <label htmlFor="recurring-new-cancel-date" className="block text-xs font-medium text-slate-700 mb-1">New Cancellation Date</label>
          <DatePicker
            id="recurring-new-cancel-date"
            value={newDate}
            min={today}
            onChange={setNewDate}
          />
        </div>
      </div>
    </Modal>
  );
}

/* ── Add Product Modal ─────────────────────────────────────────────── */

function AddProductModal({
  recurringId,
  open,
  onClose,
  onSaved,
}: {
  recurringId: number;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProductSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<ProductSearchResult | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [expireDate, setExpireDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelected(null);
      setQuantity('1');
      setExpireDate('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchProducts(q)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const handleSave = async () => {
    if (!selected) return;
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    setSaving(true);
    setError(null);
    try {
      await addEnrollmentItem(recurringId, {
        itemType: 'product',
        productId: selected.id,
        quantity: qty,
        expireDate: expireDate || undefined,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const today = todayYmd();

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Add Product to Membership"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={!selected || saving} loading={saving}>
            Add Product
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        {/* Product search */}
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-slate-700">Product <span className="text-red-500">*</span></label>
          {selected ? (
            <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{selected.name}</p>
                {selected.categoryTitle && (
                  <p className="text-xs text-slate-500">{selected.categoryTitle}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setSelected(null); setQuery(''); }}
                className="ml-2 text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products..."
                autoFocus
                className="w-full rounded border border-slate-300 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {(results.length > 0 || searching) && (
                <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {searching && results.length === 0 ? (
                    <div className="flex items-center gap-2 px-3 py-3 text-xs text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" /> Searching...
                    </div>
                  ) : (
                    results.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setSelected(p); setResults([]); setQuery(''); }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                          {p.categoryTitle && (
                            <p className="text-xs text-slate-400 truncate">{p.categoryTitle}</p>
                          )}
                        </div>
                        <span className="ml-2 flex-shrink-0 text-xs font-medium text-slate-500">
                          {fmtCurrency(p.price)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quantity */}
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-slate-700">Quantity <span className="text-red-500">*</span></label>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-24 rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Expiration date */}
        <div className="space-y-1.5">
          <label htmlFor="recurring-product-expire-date" className="block text-xs font-medium text-slate-700">Expiration Date</label>
          <DatePicker
            id="recurring-product-expire-date"
            value={expireDate}
            min={today}
            onChange={setExpireDate}
            clearable
            wrapperClassName="w-48"
          />
          <p className="text-xs text-slate-400">Leave empty for no expiration</p>
        </div>
      </div>
    </Modal>
  );
}

/* ── Charged card note ────────────────────────────────────────────── */

/**
 * The card the recurring cron charges (the profile's stored token), and whether
 * it is still on the patient: `removed` = its card row was ended, `missing` = no
 * card row at all (e.g. a terminal capture that charged but could not be saved).
 */
interface StoredCard {
  token: string;
  label: string;
  status: 'onFile' | 'removed' | 'missing';
}

/** True for a token Save can store: a card, not "new", a terminal or a manual method. */
function isSavableCardToken(token: string): boolean {
  return token !== '' && token !== 'new' && !token.startsWith('terminal|') && !token.startsWith('manual|');
}

/** Says which card the recurring charge actually uses, and what Save will change. */
function ChargedCardNote({ storedCard, selectedToken }: {
  storedCard: StoredCard | null;
  selectedToken: string;
}) {
  if (!storedCard) {
    return <p className="text-xs text-slate-500">No card is saved on this recurring payment.</p>;
  }
  if (isSavableCardToken(selectedToken) && selectedToken !== storedCard.token) {
    return (
      <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
        Currently charging <span className="font-semibold">{storedCard.label}</span>.
        {' '}The selected card replaces it when you click Save.
      </p>
    );
  }
  if (storedCard.status !== 'onFile') {
    return (
      <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <p>
          {storedCard.status === 'removed' ? (
            <>
              <span className="font-semibold">{storedCard.label}</span> was removed from the patient&apos;s
              profile, but this recurring payment still charges it.
            </>
          ) : (
            <>
              This recurring payment charges a card that isn&apos;t saved on the patient&apos;s profile, so its
              details can&apos;t be shown. It is still charged.
            </>
          )}
          {' '}Choose another card and click Save to switch.
        </p>
      </div>
    );
  }
  return (
    <p className="text-xs text-slate-500">
      Currently charging <span className="font-medium text-slate-700">{storedCard.label}</span>.
    </p>
  );
}

/* ── Collapsible Section ──────────────────────────────────────────── */

function CollapsibleSection({ title, defaultOpen = false, nested, headerExtra, children }: {
  title: string;
  defaultOpen?: boolean;
  nested?: boolean;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (nested) {
    return (
      <div className="border-t border-slate-200 pt-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex w-full items-center gap-2 text-left"
        >
          <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className="text-xs font-semibold text-slate-600">{title}</span>
        </button>
        {open && <div className="mt-2">{children}</div>}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center bg-slate-50 px-4 py-2">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
          <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        </button>
        {headerExtra && (
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {headerExtra}
          </div>
        )}
      </div>
      {open && <div className="border-t border-slate-200">{children}</div>}
    </div>
  );
}

/* ── Payment History Table (matching PHP: Date, Status, Paid Through, Amount) ── */

function PaymentHistoryTable({
  records,
  onPaymentClick,
}: {
  records: PaymentHistoryRecord[];
  onPaymentClick?: (paymentId: number) => void;
}) {
  const filtered = useMemo(
    () => records.filter((r) => r.status.toLowerCase().trim() !== 'upcoming'),
    [records],
  );

  if (filtered.length === 0) {
    return <p className="py-3 text-center text-xs italic text-slate-400">there are no results</p>;
  }

  return (
    <table className="min-w-full text-xs">
      <thead>
        <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          <th className="px-2 py-1.5">Date</th>
          <th className="px-2 py-1.5">Status</th>
          <th className="px-2 py-1.5">Paid Through</th>
          <th className="px-2 py-1.5 text-right">Amount</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {filtered.map((r) => {
          const statusText = normalizeHistoryStatus(r.status);
          const normalizedStatus = r.status.toLowerCase().trim();
          const isDeclined = normalizedStatus === 'declined';
          const isFrozen = normalizedStatus === 'frozen';
          const isClickable = r.paymentId !== 0 && onPaymentClick;
          return (
            <tr
              key={r.id}
              className={[
                isDeclined ? 'bg-red-50' : isFrozen ? 'bg-blue-50' : '',
                isClickable ? 'cursor-pointer hover:bg-slate-50' : '',
              ].join(' ')}
              onClick={isClickable ? () => onPaymentClick(r.paymentId) : undefined}
            >
              <td className="px-2 py-1 text-slate-700">{fmtDate(r.paymentDate)}</td>
              <td className="px-2 py-1 text-slate-500">{statusText}</td>
              <td className="px-2 py-1 text-slate-500">{fmtDate(r.paymentUntilDate)}</td>
              <td className="px-2 py-1 text-right text-slate-700">{fmtCurrency(r.amount)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* ── Main Modal ────────────────────────────────────────────────────── */

interface RecurringDetailModalProps {
  recurringId: number | null;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  onPaymentClick?: (paymentId: number) => void;
}

export function RecurringDetailModal({ recurringId, open, onClose, onChanged, onPaymentClick }: RecurringDetailModalProps) {
  const { hasMinUserLevel } = useAuth();
  const queryClient = useQueryClient();
  const canEditPrice = hasMinUserLevel(2);
  const canAddProduct = hasMinUserLevel(3);
  const canSeeRawEndDate = hasMinUserLevel(5);
  const canSeeCanceledBy = hasMinUserLevel(3);

  const [detail, setDetail] = useState<RecurringDetailData | null>(null);
  const [history, setHistory] = useState<PaymentHistoryRecord[]>([]);
  const [reasons, setReasons] = useState<CancellationReason[]>([]);
  const [template, setTemplate] = useState<MembershipTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);

  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentToken, setPaymentToken] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(null);
  const [paymentMethodTitle, setPaymentMethodTitle] = useState('');
  const [, setSelectedMethod] = useState<BillingMethod | null>(null);
  const [selectedTerminal, setSelectedTerminal] = useState<PaymentTerminal | null>(null);
  const [, setTerminalResult] = useState<TerminalFlowResult | null>(null);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  const [nextBillingDate, setNextBillingDate] = useState('');
  const [startDate, setStartDate] = useState('');

  const [saveOverlay, setSaveOverlay] = useState<'saving' | 'saved' | null>(null);
  const [retrying, setRetrying] = useState(false);

  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [facilityName, setFacilityName] = useState('');

  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const initialSnapshotRef = useRef('');
  const formReadyRef = useRef(false);
  const snapshotPendingRef = useRef(false);

  const handlePaymentClick = useCallback((paymentId: number) => {
    if (onPaymentClick) {
      onPaymentClick(paymentId);
    } else {
      setSelectedPaymentId(paymentId);
    }
  }, [onPaymentClick]);

  const [productAppt, setProductAppt] = useState<Appointment | null>(null);
  const handleOpenAppointment = useCallback(async (appointmentId: number) => {
    try {
      const appt = await appointmentsService.getAppointment(appointmentId);
      setProductAppt(appt);
    } catch { /* ignore */ }
  }, []);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [allFreezeRecords, setAllFreezeRecords] = useState<FreezeRecord[]>([]);
  const [upcomingFreezes, setUpcomingFreezes] = useState<FreezeRecord[]>([]);

  const [cancelReasonId, setCancelReasonId] = useState<string>('0');
  const [cancelOtherText, setCancelOtherText] = useState('');

  const [productData, setProductData] = useState<ProductTypesResponse | null>(null);
  const [productRefreshKey, setProductRefreshKey] = useState(0);

  const membershipIds = useMemo(
    () => new Set((detail?.memberships ?? []).map((m) => m.membershipId).filter(Boolean)),
    [detail?.memberships],
  );

  const filteredProducts = useMemo(() => {
    if (!productData || membershipIds.size === 0) return { types: [], others: [] };
    return filterProductsByMembership(productData, membershipIds);
  }, [productData, membershipIds]);

  useEffect(() => {
    if (!detail?.patientId) { setProductData(null); return; }
    let cancelled = false;
    getPatientProductTypes(String(detail.patientId)).then((res: ProductTypesResponse) => {
      if (!cancelled) setProductData(res);
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [detail?.patientId, productRefreshKey]);

  const loadDetail = useCallback(async () => {
    if (!recurringId) return;
    formReadyRef.current = false;
    setLoading(true);
    setError(null);
    try {
      const [d, h, r, freezeRecs] = await Promise.all([
        getRecurringDetail(recurringId),
        getPaymentHistory(recurringId),
        listCancellationReasons(),
        getFreezeRecords(recurringId),
      ]);

      setDetail(d);
      setHistory(h);
      setReasons(r);
      setAllFreezeRecords(freezeRecs);
      setUpcomingFreezes(freezeRecs.filter((fr) => fr.status === 'frozen'));
      setAmount(d.amount != null ? Number(d.amount).toFixed(2) : '0.00');
      setNotes(d.notes ?? '');

      const methods = Array.isArray(d.allPaymentMethods) ? d.allPaymentMethods : [];
      if (!methods.length && d.paymentMethod) {
        methods.push(d.paymentMethod);
        d.allPaymentMethods = methods;
      }

      // Always select the card the profile actually charges, even when it is no
      // longer an active card on the patient. Falling back to another card made
      // the modal show a card that was never saved (SCRUM-2257).
      const storedToken = d.paymentMethodToken;
      setPaymentToken(storedToken);
      const matchedPm = methods.find((m) => m.token === storedToken);
      setPaymentMethodId(
        matchedPm?.paymentMethodID
          ?? d.paymentMethod?.paymentMethodID
          ?? null,
      );
      setPaymentMethodTitle(matchedPm?.title ?? d.paymentMethod?.title ?? d.strPayMethod ?? '');
      setNextBillingDate(toDateInput(d.nextBillingDate ?? d.pauseUntilDate));
      setStartDate(toDateInput(d.firstBillingDate));
      // An "Other" cancellation is stored as membershipReasonID 0 plus free text,
      // so map it back onto the picker's 'other' value — otherwise the select
      // would read as "Select one..." and saving would fail validation with no
      // way to see what the recorded reason actually was.
      const loadedReasonId = d.cancelReasonId ?? '0';
      const loadedOtherText = d.cancelOtherReason ?? '';
      setCancelReasonId(
        (loadedReasonId === '0' || loadedReasonId === '') && loadedOtherText.trim()
          ? 'other'
          : loadedReasonId,
      );
      setCancelOtherText(loadedOtherText);

      const membership = d.memberships?.[0];
      const templateId = membership?.templateId ?? membership?.membershipId;
      if (templateId && templateId > 0) {
        try {
          const tmpl = await getTemplate(templateId);
          setTemplate(tmpl);
        } catch {
          setTemplate(null);
        }
      } else {
        setTemplate(null);
      }

      if (d.facilityID) {
        try {
          const facilities = await listFacilities();
          const match = facilities.find((f) => f.facilityId === d.facilityID);
          setFacilityName(match?.facilityName ?? '');
        } catch {
          setFacilityName('');
        }
      } else {
        setFacilityName('');
      }
      snapshotPendingRef.current = true;
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [recurringId]);

  useEffect(() => {
    if (open && recurringId) {
      void loadDetail();
    }
    if (!open) {
      setDetail(null);
      setTemplate(null);
      setError(null);
      setNotice(null);
      setSaveOverlay(null);
      setCancelOpen(false);
      setFreezeOpen(false);
      setAddProductOpen(false);
      setSelectedPaymentId(null);
      setSelectedMethod(null);
      setSelectedTerminal(null);
      setTerminalResult(null);
      setSaveWarning(null);
    }
  }, [open, recurringId, loadDetail]);

  const today = todayYmd();
  const incrementType = detail?.incrementType || '1';
  const isRecurring = incrementType !== '0';
  const isCancelled = incrementType === '0';
  const canReactivate = (() => {
    if (!isCancelled || !detail) return false;
    const reactivationYmd = detail.reactivationThroughDate ? detail.reactivationThroughDate.split(' ')[0] : null;
    if (reactivationYmd && reactivationYmd >= today) return true;
    const throughYmd = detail.paymentThroughDate ? detail.paymentThroughDate.split(' ')[0] : null;
    return throughYmd ? throughYmd >= today : false;
  })();

  const hasUpcomingRecords = allFreezeRecords.length > 0;
  const canAdjustCancelDate = isCancelled && detail?.endDate
    ? detail.endDate.split(' ')[0] > today
    : false;

  const templateProducts = template?.products ?? [];
  const membershipTitle = template?.title ?? detail?.memberships?.[0]?.title ?? '';

  const statusDisplay = useMemo(() => {
    if (!detail) return null;
    const endYmd = detail.endDate ? detail.endDate.split(' ')[0] : null;
    const reactivationYmd = detail.reactivationThroughDate ? detail.reactivationThroughDate.split(' ')[0] : null;

    if (detail.incrementType === '0') {
      const canStillReactivate = reactivationYmd ? reactivationYmd >= today : (endYmd ? endYmd > today : false);
      if (canStillReactivate) return { text: `Set to Cancel on ${fmtDate(detail.reactivationThroughDate ?? detail.paymentThroughDate)}`, color: 'text-red-600' };
      return { text: 'Canceled', color: 'text-red-600' };
    }
    if (detail.currentRecordStatus === 'frozen') return { text: `Frozen ${fmtDate(detail.frozenFromDate)} - ${fmtDate(detail.frozenToDate)}`, color: 'text-blue-600' };
    if (detail.currentRecordStatus === '') {
      const firstYmd = detail.firstBillingDate ? detail.firstBillingDate.split(' ')[0] : null;
      if (firstYmd && firstYmd > today) return { text: `Membership Starts on ${fmtDate(detail.firstBillingDate)}`, color: 'text-green-600' };
      return { text: `Membership Paused Until ${fmtDate(detail.nextBillingDate ?? detail.pauseUntilDate)}`, color: 'text-amber-600' };
    }
    return { text: 'Recurring Monthly', color: 'text-green-600' };
  }, [detail, today]);

  /** The card the recurring cron will charge: the token stored on the profile. */
  const storedCard = useMemo((): StoredCard | null => {
    if (!detail?.paymentMethodToken) return null;
    const pm = detail.paymentMethod;
    // No card row: strPayMethod is often a payment type ("Credit Card"), not a
    // card, so don't present it as one.
    if (!pm) {
      return { token: detail.paymentMethodToken, label: 'Card not on file', status: 'missing' };
    }
    return {
      token: detail.paymentMethodToken,
      label: pm.title || (pm.lastFour ? `Card ending ${pm.lastFour}` : 'Saved card'),
      status: pm.active === false ? 'removed' : 'onFile',
    };
  }, [detail]);

  /* ── Unsaved changes tracking ─────────────────────────────────── */

  const currentSnapshot = useMemo(() => JSON.stringify({
    amount, paymentToken, nextBillingDate, startDate, cancelReasonId, cancelOtherText,
  }), [amount, paymentToken, nextBillingDate, startDate, cancelReasonId, cancelOtherText]);

  useEffect(() => {
    if (snapshotPendingRef.current && !loading) {
      initialSnapshotRef.current = currentSnapshot;
      formReadyRef.current = true;
      snapshotPendingRef.current = false;
    }
  }, [currentSnapshot, loading]);

  const hasUnsavedChanges = formReadyRef.current && initialSnapshotRef.current !== '' && currentSnapshot !== initialSnapshotRef.current;

  useEffect(() => {
    if (!open) {
      initialSnapshotRef.current = '';
      formReadyRef.current = false;
      snapshotPendingRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !hasUnsavedChanges) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [open, hasUnsavedChanges]);

  useEffect(() => {
    if (!open || !hasUnsavedChanges) return;
    const handlePopState = () => {
      const leave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!leave) {
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [open, hasUnsavedChanges]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const confirmClose = useCallback(() => {
    setShowUnsavedConfirm(false);
    onClose();
  }, [onClose]);

  /* ── Handlers ─────────────────────────────────────────────────── */

  const isZeroAmount = (parseFloat(amount) || 0) === 0;

  const handleSave = async () => {
    if (!recurringId || !detail) return;
    if ((!paymentToken || paymentToken === 'new') && !isZeroAmount) {
      setError('Select a payment source or add a new card before saving.');
      return;
    }
    if (isCancelled) {
      if (cancelReasonId === '0' || (cancelReasonId === 'other' && !cancelOtherText.trim())) {
        setError('Please enter a cancellation reason.');
        return;
      }
    }

    setSaveOverlay('saving');
    setError(null);
    setNotice(null);
    try {
      const firstYmd = detail.firstBillingDate ? detail.firstBillingDate.split(' ')[0].split('T')[0] : null;
      const hasNotStarted = !!(firstYmd && firstYmd >= today);
      const effectiveStartDate = startDate || toDateInput(detail.firstBillingDate) || undefined;

      let effectiveNextBilling = nextBillingDate || undefined;
      let derivedChargeDay: number;

      if (hasNotStarted && effectiveStartDate) {
        derivedChargeDay = parseInt(effectiveStartDate.split('-')[2], 10) || Number(detail.chargeDay) || 1;
        effectiveNextBilling = effectiveStartDate;
      } else {
        derivedChargeDay = nextBillingDate
          ? parseInt(nextBillingDate.split('-')[2], 10) || Number(detail.chargeDay) || 1
          : Number(detail.chargeDay) || 1;
      }

      const result = await saveRecurringPayment(recurringId, {
        paymentMethodToken: paymentToken,
        paymentMethodID: paymentMethodId ?? undefined,
        strPayMethod: paymentMethodTitle || detail.strPayMethod,
        accountMerchantId: detail.accountMerchantId ?? undefined,
        firstBillingDate: effectiveStartDate,
        nextBillingDate: effectiveNextBilling,
        chargeDay: derivedChargeDay,
        incrementType,
        amount: parseFloat(amount) || 0,
        notes,
        cancelReason: isCancelled ? cancelReasonId : undefined,
        otherReason: isCancelled && cancelReasonId === 'other' ? cancelOtherText : undefined,
      });

      if (result.chargeNow) {
        setNotice(chargeResultMessage('Save and charge', result.chargeResult));
      }
      onChanged();
      await loadDetail();
      initialSnapshotRef.current = currentSnapshot;
      setSaveOverlay('saved');
      await new Promise((r) => setTimeout(r, 800));
      setSaveOverlay(null);
    } catch (err) {
      setSaveOverlay(null);
      setError(getErrorMessage(err));
    }
  };

  const handleCancelConfirm = async (reasonId: string, otherText: string, cancelDate: string) => {
    if (!recurringId) return;
    setCancelling(true);
    setError(null);
    try {
      await cancelRecurringPayment(
        recurringId,
        reasonId !== '0' ? reasonId : undefined,
        reasonId === 'other' ? otherText.trim() : undefined,
        cancelDate || undefined,
      );
      setCancelOpen(false);
      setNotice({ tone: 'success', text: 'Recurring payment canceled.' });
      onChanged();
      await loadDetail();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    if (!recurringId || !confirm('Are you sure you want to reactivate this recurring payment?')) return;
    setError(null);
    try {
      await reactivateRecurring(recurringId);
      setNotice({ tone: 'success', text: 'Recurring payment reactivated.' });
      onChanged();
      await loadDetail();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleAdjustCancelDate = async (newDate: string) => {
    if (!recurringId || !confirm('Are you sure you want to adjust the cancellation date?')) return;
    setAdjusting(true);
    setError(null);
    try {
      await adjustCancelDate(recurringId, newDate);
      setAdjustOpen(false);
      setNotice({ tone: 'success', text: 'Cancellation date adjusted.' });
      onChanged();
      await loadDetail();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setAdjusting(false);
    }
  };

  const handleUnfreeze = async () => {
    if (!recurringId || !confirm("Are you sure you want to unfreeze this membership? The patient's billing date will be changed to reflect today's date and they will be charged now.")) return;
    setError(null);
    try {
      const result = await unfreezeAndCharge(recurringId);
      setNotice(chargeResultMessage('Unfreeze and charge', result.chargeResult));
      onChanged();
      await loadDetail();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleRetryPayment = async () => {
    if (!recurringId) return;
    setRetrying(true);
    setError(null);
    try {
      const result = await retryRecurringPayment(recurringId);
      setNotice(chargeResultMessage('Retry payment', result));
      onChanged();
      await loadDetail();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setRetrying(false);
    }
  };

  const isTerminalMode = parseTerminalToken(paymentToken) !== null;

  const handleSourceChange = useCallback(
    (token: string, method: BillingMethod | null, terminal: PaymentTerminal | null = null) => {
      setPaymentToken(token);
      setSelectedMethod(method);
      setSelectedTerminal(terminal);
      setTerminalResult(null);
      setSaveWarning(null);
      // Re-picking a removed stored card has no BillingMethod row; keep its saved details.
      if (!method && storedCard && token === storedCard.token) {
        setPaymentMethodId(detail?.paymentMethod?.paymentMethodID ?? null);
        setPaymentMethodTitle(detail?.paymentMethod?.title ?? detail?.strPayMethod ?? '');
        return;
      }
      setPaymentMethodId(method?.paymentMethodID ?? null);
      setPaymentMethodTitle(method?.title ?? '');
    },
    [storedCard, detail],
  );

  const handleNewSourceSaved = useCallback((method: BillingMethod) => {
    setSelectedMethod(method);
    setPaymentToken(method.token);
    setSelectedTerminal(null);
    setTerminalResult(null);
    setSaveWarning(null);
    setPaymentMethodId(method.paymentMethodID ?? null);
    setPaymentMethodTitle(method.title ?? '');
    setError(null);
  }, []);

  const handleTerminalResult = useCallback(
    (result: TerminalFlowResult) => {
      setTerminalResult(result);
      setSaveWarning(result.data.save_warning ?? null);
      if (result.mode === 'tokenize') {
        const token = result.data.token;
        if (token) {
          setPaymentToken(token);
          // The terminal just saved this card; refetch so the dropdown has an option
          // for it instead of falling back to the first option it does have.
          if (detail?.patientId) {
            void queryClient.invalidateQueries({ queryKey: ['patient-billing-methods', detail.patientId] });
          }
        }
      }
    },
    [queryClient, detail?.patientId],
  );

  const handleTerminalReset = useCallback(() => {
    setTerminalResult(null);
    setSaveWarning(null);
  }, []);

  /* ── Footer buttons ───────────────────────────────────────────── */

  const footerContent = detail ? (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-2">
        {isRecurring && (
          <>
            <Button size="sm" variant="danger" leftIcon={<Ban className="h-3.5 w-3.5" />} onClick={() => setCancelOpen((c) => !c)}>
              Cancel
            </Button>
            <Button size="sm" variant="secondary" leftIcon={<Snowflake className="h-3.5 w-3.5" />} onClick={() => setFreezeOpen(true)}>
              Freeze
            </Button>
          </>
        )}
        {isCancelled && hasUpcomingRecords && !isRecurring && (
          <Button size="sm" variant="secondary" leftIcon={<Snowflake className="h-3.5 w-3.5" />} onClick={() => setFreezeOpen(true)}>
            Freeze
          </Button>
        )}
        {canReactivate && (
          <Button size="sm" variant="secondary" leftIcon={<RotateCcw className="h-3.5 w-3.5" />} onClick={() => void handleReactivate()}>
            Reactivate
          </Button>
        )}
        {canAdjustCancelDate && (
          <Button size="sm" variant="secondary" onClick={() => setAdjustOpen(true)}>
            Adjust Cancel Date
          </Button>
        )}
        {detail.declined === '1' && detail.retryPaymentId ? (
          <Button size="sm" variant="secondary" leftIcon={<RefreshCw className="h-3.5 w-3.5" />} onClick={() => void handleRetryPayment()} disabled={retrying} loading={retrying}>
            Retry Payment
          </Button>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        {hasUnsavedChanges && (
          <span className="text-xs font-medium text-amber-600">Unsaved changes</span>
        )}
        <Button size="sm" leftIcon={<Save className="h-3.5 w-3.5" />} onClick={() => void handleSave()} disabled={saveOverlay !== null || (!paymentToken && !isZeroAmount) || isTerminalMode}>
          Save
        </Button>
      </div>
    </div>
  ) : undefined;

  /* ── Render ───────────────────────────────────────────────────── */

  const headerStatus = detail
    ? (detail.incrementType === '0' ? 'Canceled' : 'Active')
    : null;

  const modalTitle = detail ? (
    <div className="flex items-center gap-3">
      <div className="min-w-0">
        <Link
          to={`/patients/${detail.patientId}`}
          className="text-xs font-medium text-blue-600 hover:underline"
          target="_blank"
          onClick={(e) => e.stopPropagation()}
        >
          {detail.patientName}
        </Link>
        <div className="truncate text-base font-bold text-slate-900">
          {membershipTitle || 'Membership'}
        </div>
        {facilityName && (
          <div className="truncate text-xs text-slate-500">{facilityName}</div>
        )}
      </div>
      {headerStatus && (
        <span className={`ml-auto mr-4 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
          headerStatus === 'Canceled' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
        }`}>
          {headerStatus}
        </span>
      )}
    </div>
  ) : 'Membership';

  return (
    <>
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={modalTitle}
      size="xl"
      footer={footerContent}
      bodyClassName="!p-0 !overflow-hidden flex flex-col"
      overlay={
        saveOverlay ? (
          <div className="flex flex-col items-center gap-3">
            {saveOverlay === 'saving' ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="text-sm font-medium text-slate-600">Saving…</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                <span className="text-sm font-medium text-green-700">Saved</span>
              </>
            )}
          </div>
        ) : undefined
      }
    >
      {loading && !detail ? (
        <div className="flex items-center justify-center py-12">
          <span className="text-sm text-slate-400">Loading...</span>
        </div>
      ) : error && !detail ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 mx-6 my-5 text-sm text-red-700">{error}</div>
      ) : detail ? (
        <>
          {/* Fixed banner */}
          <div className="flex-shrink-0 space-y-2 px-6 pt-2 pb-2">
            <StatusBanner detail={detail} onUnfreeze={() => void handleUnfreeze()} />

            {notice && notice.tone === 'warning' && (
              <div className={`rounded-lg border px-4 py-2 text-sm ${toneClasses(notice.tone)}`}>{notice.text}</div>
            )}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
            )}
          </div>

          {/* Scrollable content */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-5 space-y-5">

          {/* ── SECTION 1: Membership Title Card ── */}
          <MembershipTitleCard
            detail={detail}
            templateProducts={templateProducts}
            nextBillingDate={nextBillingDate}
            onNextBillingChange={setNextBillingDate}
            monthlyAmount={amount}
            onAmountChange={setAmount}
            startDate={startDate}
            onStartDateChange={setStartDate}
            isRecurring={isRecurring}
            amountEditable={canEditPrice}
            statusDisplay={statusDisplay}
            reasons={reasons}
            cancelReasonId={cancelReasonId}
            cancelOtherText={cancelOtherText}
            onCancelReasonChange={(value) => { setCancelReasonId(value); setError(null); }}
            onCancelOtherTextChange={(value) => { setCancelOtherText(value); setError(null); }}
            showRawEndDate={canSeeRawEndDate}
            showCanceledBy={canSeeCanceledBy}
          />

          {/* ── Upcoming Freezes ── */}
          {upcomingFreezes.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
              <div className="flex items-center gap-2 mb-2">
                <Snowflake className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-semibold text-blue-700">
                  Upcoming Freeze{upcomingFreezes.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="space-y-1">
                {upcomingFreezes.map((fr) => (
                  <div key={fr.paymentsRecurringRecordID} className="flex items-center gap-2 text-sm text-blue-600">
                    <Snowflake className="h-3 w-3 flex-shrink-0" />
                    <span>{fmtDate(fr.paymentDate)}</span>
                    <span className="text-blue-400">—</span>
                    <span>{fmtDate(fr.paymentUntilDate)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── SECTION 2: Products (collapsible) ── */}
          <CollapsibleSection
            title="Products"
            defaultOpen={false}
            headerExtra={!isCancelled && canAddProduct ? (
              <button
                type="button"
                onClick={() => setAddProductOpen(true)}
                className="flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
                title="Add product"
              >
                <Plus className="h-3.5 w-3.5" />
                Add
              </button>
            ) : undefined}
          >
            {!productData ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                <span className="ml-2 text-xs text-slate-400">Loading products…</span>
              </div>
            ) : filteredProducts.types.length === 0 && filteredProducts.others.length === 0 ? (
              <p className="py-3 text-center text-xs italic text-slate-400">No products for this membership</p>
            ) : (
              <div className="space-y-1 p-2">
                {filteredProducts.types.map((g) => (
                  <ProductGroupCard
                    key={`pt-${g.productTypeID}`}
                    group={g}
                    listType="product-types"
                    onAction={async () => {}}
                    onOpenAppointment={handleOpenAppointment}
                    readOnly
                  />
                ))}
                {filteredProducts.others.map((g) => (
                  <ProductGroupCard
                    key={`po-${g.productID ?? g.productTypeID}`}
                    group={g}
                    listType="product-others"
                    onAction={async () => {}}
                    onOpenAppointment={handleOpenAppointment}
                    readOnly
                  />
                ))}
              </div>
            )}
          </CollapsibleSection>

          {/* ── SECTION 3: Payment & Billing (collapsible) ── */}
          <CollapsibleSection
            title="Payment & Billing"
            defaultOpen={storedCard != null && storedCard.status !== 'onFile'}
            headerExtra={storedCard ? (
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                Charging{' '}
                {storedCard.status === 'missing' ? (
                  <span className="font-medium text-amber-800">a card not on file</span>
                ) : (
                  <span className="font-medium text-slate-700">{storedCard.label}</span>
                )}
                {storedCard.status === 'removed' && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">Removed card</span>
                )}
              </span>
            ) : undefined}
          >
            <div className="space-y-4 p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <PaymentSourceSelector
                  patientId={detail.patientId}
                  value={paymentToken}
                  merchantId={detail.accountMerchantId ? Number(detail.accountMerchantId) : null}
                  onChange={handleSourceChange}
                  retainedCard={storedCard ? {
                    token: storedCard.token,
                    label: storedCard.status === 'removed' ? `${storedCard.label} (removed)` : storedCard.label,
                    groupLabel: 'Currently charged',
                  } : null}
                  placeholder="Select a payment source"
                />
                <MerchantSelector
                  value={detail.accountMerchantId ? Number(detail.accountMerchantId) : null}
                  onChange={() => {}}
                  disabled
                />
              </div>
              <ChargedCardNote storedCard={storedCard} selectedToken={paymentToken} />
              <ForwardBillingMethodCapture
                active={paymentToken === 'new'}
                patientId={detail.patientId}
                merchantId={detail.accountMerchantId ? Number(detail.accountMerchantId) : null}
                onSaved={handleNewSourceSaved}
              />
              {isTerminalMode && selectedTerminal ? (
                <TerminalCheckoutPanel
                  terminal={selectedTerminal}
                  patientId={detail.patientId}
                  amount={0}
                  merchantId={detail.accountMerchantId ? Number(detail.accountMerchantId) : null}
                  mode="tokenize"
                  onSuccess={handleTerminalResult}
                  onReset={handleTerminalReset}
                />
              ) : null}
              {saveWarning ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <p className="font-semibold">Payment method not saved.</p>
                  <p className="mt-1 text-xs">{saveWarning}</p>
                </div>
              ) : null}

              {/* Payment History (collapsible sub-section) */}
              <CollapsibleSection title="Payment History" defaultOpen={false} nested>
                <div className="px-1 py-1">
                  <PaymentHistoryTable records={history} onPaymentClick={handlePaymentClick} />
                </div>
              </CollapsibleSection>
            </div>
          </CollapsibleSection>

          {/* ── SECTION 4: Membership Agreement (collapsible) ── */}
          <CollapsibleSection title="Membership Agreement" defaultOpen={false}>
            {detail.membershipAgreement ? (
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{detail.membershipAgreement.title}</p>
                    <p className="text-xs text-slate-500">Version {detail.membershipAgreement.version}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Agreed on</p>
                    <p className="text-sm font-medium text-slate-700">{fmtDateTime(detail.membershipAgreement.agreedDate)}</p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 border-t border-slate-100 pt-3">
                  <div>
                    <span className="text-xs font-medium text-slate-500">Signed Name</span>
                    <p className="text-sm text-slate-800">{detail.membershipAgreement.signedName}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-slate-500">Date of Birth</span>
                    <p className="text-sm text-slate-800">{fmtDate(detail.membershipAgreement.signedBirthDate)}</p>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <p className="text-xs font-medium text-slate-500 mb-2">Agreement Content</p>
                  <div
                    className="prose prose-sm max-w-none rounded-lg border border-slate-200 bg-slate-50 p-4 max-h-80 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: detail.membershipAgreement.bodyHtml }}
                  />
                </div>
              </div>
            ) : (
              <div className="p-4 space-y-2">
                <p className="text-sm text-slate-600">
                  This patient did not sign a digital membership agreement. Their signed agreement can be found in the forms section.
                </p>
                <Link
                  to={`/patients/${detail.patientId}?section=forms`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  onClick={onClose}
                >
                  <FileText className="h-3.5 w-3.5" />
                  View Forms
                </Link>
              </div>
            )}
          </CollapsibleSection>

          </div>
        </>
      ) : null}
    </Modal>

    {/* ── Cancel Modal (separate popup) ── */}
    {detail && (
      <CancelModal
        detail={detail}
        reasons={reasons}
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={(rId, oText, cDate) => void handleCancelConfirm(rId, oText, cDate)}
        cancelling={cancelling}
      />
    )}

    {detail && (
      <AdjustCancelDateModal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onConfirm={(newDate) => void handleAdjustCancelDate(newDate)}
        currentCancelDate={detail.endDate ?? ''}
        adjusting={adjusting}
      />
    )}

    {/* ── Freeze Records Modal (separate popup) ── */}
    {recurringId && (
      <FreezeRecordsModal
        recurringId={recurringId}
        open={freezeOpen}
        onClose={() => setFreezeOpen(false)}
        onSaved={() => { void loadDetail(); onChanged(); }}
      />
    )}

    {/* ── Add Product Modal ── */}
    {recurringId && (
      <AddProductModal
        recurringId={recurringId}
        open={addProductOpen}
        onClose={() => setAddProductOpen(false)}
        onSaved={() => { setProductRefreshKey((k) => k + 1); }}
      />
    )}

    {/* ── Payment Detail Modal (opens when clicking a history row) ── */}
    {detail && selectedPaymentId != null && (
      <PaymentDetailModal
        paymentId={selectedPaymentId}
        patientId={detail.patientId}
        open={selectedPaymentId != null}
        onClose={() => setSelectedPaymentId(null)}
      />
    )}

    {/* ── Appointment Detail Modal (from product rows) ── */}
    {productAppt && (
      <AppointmentDetailModal
        isOpen
        onClose={() => { setProductAppt(null); setProductRefreshKey((k) => k + 1); }}
        appointment={productAppt}
        onUpdated={() => { setProductAppt(null); setProductRefreshKey((k) => k + 1); }}
      />
    )}

    {/* ── Unsaved changes confirmation ── */}
    <Modal
      isOpen={showUnsavedConfirm}
      onClose={() => setShowUnsavedConfirm(false)}
      size="sm"
      hideCloseButton
      closeOnBackdrop={false}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setShowUnsavedConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmClose}>
            Discard Changes
          </Button>
        </div>
      }
    >
      <div className="flex gap-3 items-start">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Unsaved Changes</h3>
          <p className="text-sm text-slate-500">
            You have unsaved changes. Are you sure you want to close? Your changes will be lost.
          </p>
        </div>
      </div>
    </Modal>
    </>
  );
}
